import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
  Configuration,
  initialize,
  Page,
  PageModel,
} from '@bloomreach/spa-sdk';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { ComponentMapping } from '@services/module-loader.config';
import { ModuleLoaderService } from '@services/module-loader.service';
import { GlobalConfigService } from '@services/global-config-service';
import { LabelLoader } from '@utils/label-loader';
import { from, ReplaySubject, Subject, Observable, TimeoutError } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
  timeout,
} from 'rxjs/operators';
import { Logger } from '@utils/logger';
import { AppStateService } from '@services/app-state.service';
import { HttpClient, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { LabelsService } from '@services/labels.service';
import { replaceTokens } from '@utils/text/string-utils';
import { EnvConfig, EnvConfigService } from '@services/env-config.service';
import { ProfileService } from '@services/profile.service';
import { IUserProfile, IUserProfileInfo } from '@services/profile.interface';
import { Meta } from '@angular/platform-browser';
import { LOCATION } from '@ng-web-apis/common';
import { IAnalyticsDataStore } from '@types';
import { StorageService } from '@services/storage.service';
import { SchemaService } from '@services/schema.service';
import { SegmentService } from '@services/segment.service';
import { TranslateService } from '@shared/translate/translate.service';

const logger = Logger.getLogger('PageContainerService');

export type PageConfig = {
  page?: Page;
  configuration: Configuration;
  mapping?: ComponentMapping;
  segmentOk?: boolean;
};
export interface IsGateway {
  isGateway: boolean;
  gatewayURL: string;
}
@Injectable({
  providedIn: 'root',
})
export class PageContainerService implements OnDestroy {
  page$ = new ReplaySubject<PageConfig>(1);
  private readonly configuration: Configuration;
  private currentUrl: string;
  private newPageRequestCount = 0;
  private profileKey: string;
  private profile$: Observable<IUserProfile>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    private moduleLoader: ModuleLoaderService,
    private caveatService: CaveatService,
    private labelLoader: LabelLoader,
    private translateService: TranslateService,
    private labelsService: LabelsService,
    private globalConfigService: GlobalConfigService,
    private appStateService: AppStateService,
    private httpClient: HttpClient,
    private envConfigService: EnvConfigService,
    private profileService: ProfileService,
    private storageService: StorageService,
    private schemaService: SchemaService,
    private segmentService: SegmentService,
    private meta: Meta,
    @Inject(LOCATION) readonly locationRef: Location
  ) {
    this.configuration = {
      ...this.appStateService.getApiUrls(),
      httpClient: (
        ...[{ data: body, headers, method, url }]: Parameters<
          Configuration['httpClient']
        >
      ) => {
        return this.httpClient
          .request<PageModel>(method, url, {
            body,
            headers: headers as Record<string, string | string[]>,
            responseType: 'json',
          })
          .pipe(map((data) => ({ data })))
          .toPromise();
      },
    };

    // listen for profile
    this.profile$ = this.profileService.getUserProfile$();

    // listen for profile changes (which will include segment changes) and call newPage()
    this.profileService
      .getProfileChanges$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((profileKey: string): void => {
        this.profileKey = profileKey;
        logger.debug('new profile', profileKey, this.currentUrl);
        // this should refresh existing page, but with new segment characteristics
        if (this.currentUrl) {
          this.newPage(this.currentUrl);
        }
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // TODO: is this actually async? Doesn't seem to return a Promise
  async newPage(url: string) {
    this.currentUrl = url;
    if (this.profileKey) {
      // dont send a request until the profileKey is set
      logger.debug('newPage()', url);

      this.newPageRequestCount++;
      const pageConfig: PageConfig = {
        configuration: {
          ...this.configuration,
          // The spa sdk is going to add in elements to the path based on options and context
          // so we need to supply JUST the current path to the content.
          request: { path: url },
        },
      };

      this.envConfigService.loadEnvConfig()?.then(() => {
        const envConfig = this.envConfigService.getEnvConfig();
        from(initialize(pageConfig.configuration)) // initialize from spa-sdk calls the resourceapi
          .pipe(
            // Check for timeout
            timeout(envConfig.resourceApiTimeoutMs),
            catchError((err: any) => {
              // If Request has timed out
              if (err instanceof TimeoutError) {
                // Catch Timeout error
                if (envConfig.errorPage) {
                  this.meta.addTag({
                    name: 'prerender-status-code',
                    content: '408',
                  });

                  // Redirect user to time out page.
                  this.locationRef.href = envConfig.errorPage;
                }
              }
              // Non 200 ok http response
              if (err instanceof HttpErrorResponse) {
                if (envConfig.errorPage) {
                  this.meta.addTag({
                    name: 'prerender-status-code',
                    content: '' + err.status,
                  });
                  // Redirect user to error page.
                  this.locationRef.href = envConfig.errorPage;
                }
              }
              return new Observable<HttpEvent<any>>();
            }),
            tap((page: Page) => {
              logger.debug('processing new resourceApi response', page);
              pageConfig.page = page;
              pageConfig.segmentOk = this.segmentService.checkSegmentRestrict(
                page
              );
              this.labelsService.loadCommonLabels(page);
              this.labelsService.loadLanguageLabels(page);
              this.loadLabelModels(page);
              // load labels before config services start, so they're already available
              this.globalConfigService.initialiseFromPage(page);
              this.labelLoader.loadLabelOverrides();
              this.iterateAndReplaceTokens(page, envConfig);
              this.schemaService.removeStructuredData();
            }),
            takeWhile(() => {
              this.newPageRequestCount--;
              return this.newPageRequestCount === 0; // dont bother processing response if another request is already in progress
            }),
            switchMap((page: Page) => this.moduleLoader.getMapping$(page)),
            tap((mapping) => logger.debug('mapping', mapping)),
            take(1)
          )
          .subscribe((mapping) => {
            pageConfig.mapping = mapping;
            this.moduleLoader.initializeFootnotes(
              pageConfig.page,
              this.caveatService
            );
            this.page$.next(pageConfig);
          });
      });
    }
  }

  /**
   * Iterate over resource api response and replace tokens from strings
   * @param page - page object.
   * @param flatPage - 'flatten' jason page
   */
  private iterateAndReplaceTokens(page: Page, envConfig: EnvConfig) {
    Object.keys(page).forEach((pageKey) => {
      if (page[pageKey] !== null && typeof page[pageKey] === 'object') {
        this.iterateAndReplaceTokens(page[pageKey], envConfig);
        return;
      }
      // Profile condition causes issue with replacing tokens when it is initiated before profile is set.
      // This might happen during logout. Profile is not mandatory field for replaceTokens and can be undefined.
      if (typeof page[pageKey] === 'string' && envConfig) {
        if (
          page[pageKey].match(
            /\$(ENV|CONFIG|LABEL|PROFILE)(\(|\[)(\w+(?:.\w+)*)(\)|\])/g
          )
        ) {
          // Replace tokens if they match criteria.
          this.profile$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((profile) => {
              page[pageKey] = replaceTokens(
                page[pageKey],
                envConfig,
                profile,
                this.translateService
              );
            });
        }
      }
    });
  }

  /**
   *  Set Labels for translation in labelLoader
   * @param page - page object
   */
  loadLabelModels(page: Page) {
    const models = page.getComponent('site-configuration')?.getModels();
    if (models) {
      this.labelLoader.setLabelTranslation(models);
    }
  }

  async getPartialDimensionOnLoginStatus(
    isLoggedIn: boolean,
    profile: IUserProfileInfo
  ): Promise<IAnalyticsDataStore> {
    try {
      if (isLoggedIn) {
        return {
          expressNo: profile?.businessKey,
          userSysNo: profile?.userSysNo,
        };
      } else {
        return this.storageService.retrieveAnalyticsData();
      }
    } catch (err) {
      throw err;
    }
  }

  public getGateway(): IsGateway {
    return {
      isGateway: this.appStateService.getIsGateway(),
      gatewayURL: this.appStateService.getGatewayUrl(),
    };
  }
}
